import { LayoutDashboard, LogOut, Mail, User } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { fromE164 } from '@/lib/phone';
import { useApplicationStore, type ExistingApplication } from '@/store/applicationStore';
import { useAuthStore } from '@/store/authStore';

import { KSLogo } from '../ui/KSLogo';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const logout = useAuthStore((state) => state.logout);
  const applicant = useAuthStore((state) => state.applicant);
  const checkExistingApplications = useApplicationStore((state) => state.checkExistingApplications);
  const jobPostingId = useApplicationStore((state) => state.jobPostingId);
  const serverApplicationId = useApplicationStore((state) => state.serverApplicationId);

  const userContact = applicant?.email ?? (applicant?.phone ? fromE164(applicant.phone) : null);

  const [submittedApplication, setSubmittedApplication] = useState<ExistingApplication | null>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const isOnWizard = location.pathname === '/wizard';
  const isOnSuccess = location.pathname === '/success';

  useEffect(() => {
    if (!isUserMenuOpen) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsUserMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isUserMenuOpen]);

  // Check for existing submitted applications when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setSubmittedApplication(null);
      return;
    }

    checkExistingApplications().then((applications) => {
      const submitted = applications.find((app) => app.status === 'submitted');
      setSubmittedApplication(submitted ?? null);
    });
  }, [isAuthenticated, checkExistingApplications]);

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    logout();
    void navigate('/');
  };

  return (
    <div className='min-h-screen bg-brand-pale font-sans text-brand-gray overflow-x-hidden'>
      <a
        href='#main-content'
        className='sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-white focus:rounded focus:font-medium'>
        {t('skipToContent')}
      </a>
      <nav className='bg-white border-b border-gray-100 sticky top-0 z-50' aria-label='Main navigation'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between h-20'>
            <div className='flex items-center'>
              <Link to='/' aria-label={t('nav.logoAriaLabel')} className='flex items-center gap-3 group'>
                <KSLogo className='h-8 w-auto text-primary group-hover:text-primary-dark transition-colors' />
                <div className='flex flex-col'>
                  <span className='font-bold text-xl tracking-tight text-brand-gray leading-none'>KS SERVICES</span>
                  <span className='text-[10px] uppercase tracking-widest text-gray-500 font-medium'>
                    Security & Investigations
                  </span>
                </div>
              </Link>
            </div>
            <div className='flex items-center space-x-4'>
              {isAuthenticated ? (
                <>
                  {!isOnWizard &&
                    !isOnSuccess &&
                    (submittedApplication ? (
                      <button
                        type='button'
                        onClick={() => {
                          void navigate(
                            `/dashboard?applicationId=${encodeURIComponent(submittedApplication.applicationId)}`,
                          );
                        }}
                        className='hidden sm:block px-5 py-2 bg-primary text-white text-sm font-bold rounded hover:bg-primary-dark transition'>
                        {t('landing.hero.cta.view')}
                      </button>
                    ) : jobPostingId && serverApplicationId ? (
                      <Link
                        to='/wizard'
                        className='hidden sm:block px-5 py-2 bg-primary text-white text-sm font-bold rounded hover:bg-primary-dark transition'>
                        {t('landing.hero.cta.continue')}
                      </Link>
                    ) : (
                      <Link
                        to='/jobs'
                        className='hidden sm:block px-5 py-2 bg-primary text-white text-sm font-bold rounded hover:bg-primary-dark transition'>
                        {t('landing.hero.cta.browse')}
                      </Link>
                    ))}
                  <div ref={userMenuRef} className='relative'>
                    <button
                      type='button'
                      onClick={() => setIsUserMenuOpen((open) => !open)}
                      aria-haspopup='menu'
                      aria-expanded={isUserMenuOpen}
                      aria-label={t('nav.userMenu')}
                      className='flex items-center justify-center h-10 w-10 rounded-full bg-brand-pale text-primary hover:bg-primary hover:text-white transition'>
                      <User size={18} aria-hidden='true' />
                    </button>
                    {isUserMenuOpen ? (
                      <div
                        role='menu'
                        className='absolute right-0 mt-2 w-64 rounded-lg border border-gray-100 bg-white shadow-lg ring-1 ring-black/5 py-1 z-50'>
                        {userContact ? (
                          <>
                            <div className='px-4 py-3'>
                              <p className='text-sm font-medium text-brand-gray truncate'>{userContact}</p>
                            </div>
                            <div className='border-t border-gray-100' aria-hidden='true' />
                          </>
                        ) : null}
                        <Link
                          to='/dashboard'
                          role='menuitem'
                          onClick={() => setIsUserMenuOpen(false)}
                          className='flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-brand-gray hover:bg-brand-pale hover:text-primary transition'>
                          <LayoutDashboard size={16} aria-hidden='true' />
                          {t('nav.myApplications')}
                        </Link>
                        <Link
                          to='/email-preferences'
                          role='menuitem'
                          onClick={() => setIsUserMenuOpen(false)}
                          className='flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-brand-gray hover:bg-brand-pale hover:text-primary transition'>
                          <Mail size={16} aria-hidden='true' />
                          {t('nav.emailPreferences')}
                        </Link>
                        <div className='my-1 border-t border-gray-100' aria-hidden='true' />
                        <button
                          type='button'
                          role='menuitem'
                          onClick={handleLogout}
                          className='flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium text-brand-gray hover:bg-brand-pale hover:text-primary transition'>
                          <LogOut size={16} aria-hidden='true' />
                          {t('common.logout')}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </>
              ) : (
                <Link
                  to='/auth'
                  className='hidden sm:block px-5 py-2 bg-primary text-white text-sm font-bold rounded hover:bg-primary-dark transition'>
                  {t('landing.hero.cta.apply')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>
      <main id='main-content'>{children}</main>
    </div>
  );
}
